import {
  UserManagerActions,
  UserManagerErrors,
  UserManagerGetters,
  UserManagerMutations,
  UserManagerState,
  UserManagerUser
} from "@medapro-types/UserManager/UserManager";
import logger from "@services/ApiLogger/EventLogger.js";
import commonApi from "@services/common.js";
import permissionApi from "@services/permissions";
import rolesApi from "@services/roles";
import userLicenses from "@services/userLicenses";
import { Guid } from "@util/guid";
import _ from "lodash";
import emitter from "tiny-emitter/instance";

const defaultState = (): UserManagerState => ({
  isSaving: false,
  saveMessageSeen: true,
  isBusy: false,
  users: [],
  deletedUserIds: [],
  roles: [],
  licenses: [],
  deletedRoleIds: [],
  unassignedUsers: [],
  errors: {
    users: false,
    rolesAndPermissions: false,
    licenses: false
  }
});
const mutations: UserManagerMutations = {
  "reset-state"(state) {
    Object.assign(state, defaultState());
  },
  "notification-seen": function (state) {
    state.saveMessageSeen = true;
  },
  "notification-unseen": function (state) {
    state.saveMessageSeen = false;
  },
  "set-is-busy": (state, args) => {
    state.isBusy = args;
  },
  "set-is-saving": (state, args) => {
    state.isSaving = args;
  },
  "set-users": (state, args) => {
    state.users = [...args];
  },
  "set-roles": (state, args) => {
    state.roles = _.cloneDeep(args);
  },
  "update-user-keys-in-role": (state) => {
    state.roles.forEach((role) => {
      role.users = state.users
        .filter((user) => user.roleIds.includes(role.roleId))
        .map((user) => user.userId)
        .sort();
    });
  },
  "update-role-keys-in-user": (state) => {
    state.users.forEach((user) => {
      user.roleIds = state.roles
        .filter((role) => role.users.includes(user.userId))
        .map((role) => role.roleId)
        .sort();
    });
  },
  "set-licenses": (state, args) => {
    state.licenses = _.cloneDeep(args);
  },
  "add-deleted-user-id": function (state, id) {
    state.deletedUserIds.push(id);
  },
  "add-deleted-role-id": function (state, id) {
    state.deletedRoleIds.push(id);
  },
  "add-unassigned-user": function (state, args: string) {
    if (args != Guid.empty) {
      state.unassignedUsers.push(args);
    }
  },
  "set-section-error": function (state: UserManagerState, args: { section: string; error: any }) {
    const errors = Object.assign({}, state.errors);
    errors[args.section as keyof UserManagerErrors] = args.error;
    state.errors = errors;
  },
  "reset-errors": function (state: UserManagerState) {
    const keys: (keyof UserManagerErrors)[] = Object.keys(state.errors) as (keyof UserManagerErrors)[];
    keys.forEach((key) => (state.errors[key] = false));
  },
  "clean-up-after-save": function (state) {
    const clonedUsers = _.cloneDeep(state.users);
    clonedUsers.forEach((x) => {
      delete x.isNew;
      delete x.isModified;
      delete x.password;
    });
    const clonedRoles = _.cloneDeep(state.roles);
    clonedRoles.forEach((x) => {
      delete x.isNew;
      delete x.isModified;
    });
    state.users = clonedUsers;
    state.roles = clonedRoles;
    state.deletedUserIds = [];
    state.deletedRoleIds = [];
    state.unassignedUsers = [];
  }
};
const getters: UserManagerGetters = {
  hasUnsavedData: function (state) {
    const usersCheck: boolean = state.users.some((x) => x.isModified || x.isNew);
    const roleCheck: boolean = state.roles.some((x) => x.isModified);
    const licenseCheck: boolean = state.licenses.some((x) => x.assignedUsers.some((x) => x.userLogonId == Guid.empty)) || !!state.unassignedUsers.length;
    const deletedCheck = !!(state.deletedUserIds.length || state.deletedRoleIds.length || state.unassignedUsers.length);

    return usersCheck || roleCheck || licenseCheck || deletedCheck;
  },
  lastRunSuccess: function (state) {
    const result = Object.values(state.errors).every((x) => !x);
    return result;
  }
};
const actions: UserManagerActions = {
  loadUsersIfRequired: async ({ state, rootState, commit }) => {
    if (!state.users.length) {
      try {
        const fetchedUser: UserManagerUser[] = await commonApi.getAllUsersDetails();
        for (const user of fetchedUser) {
          if (!user.dataLanguage) user.dataLanguage = rootState.metadata?.contentLanguages?.enabled?.[0]?.languageCode ?? null;
          if (user.roleIds.length) user.roleIds.sort();
          if (!user.hasImage) {
            user.image = null;
          }
        }
        commit("set-users", fetchedUser);
      } catch (error) {
        logger.error("Usermanager.loadLicensesIfRequired", "An Error occured during loading of users", error).alsoToConsole();
      }
    }
  },
  loadRolesIfRequired: async ({ state, commit }) => {
    if (!state.roles.length) {
      try {
        const fetchedRoles = await rolesApi.getAll();

        for (const role of fetchedRoles) {
          if (role.users) role.users.sort();
        }

        commit("set-roles", fetchedRoles);
      } catch (error) {
        logger.error("Usermanager.loadLicensesIfRequired", "An Error occured during loading of roles", error).alsoToConsole();
      }
    }
  },
  loadLicensesIfRequired: async ({ state, commit }) => {
    if (!state.licenses.length) {
      try {
        const fetchedLicenses = await userLicenses.get();

        commit("set-licenses", fetchedLicenses);
      } catch (error) {
        logger.error("Usermanager.loadLicensesIfRequired", "An Error occured during loading of user licenses", error).alsoToConsole();
      }
    }
  },
  addUnassignedUser: async ({ commit }, userLogonId: string) => {
    commit("add-unassigned-user", userLogonId);
  },
  saveUsers: async ({ state, commit }) => {
    try {
      const savingPromises = [];
      const newUsers = [];
      const modifiedUsers = [];

      if (state.deletedUserIds.length) {
        savingPromises.push(commonApi.deleteUsers(state.deletedUserIds));
        await Promise.all(savingPromises);
      }

      if (state.users.some((x) => x.isModified || x.isNew)) {
        for (const user of state.users) {
          if (user.isNew) newUsers.push(user);
          if (user.isModified && !user.isNew) modifiedUsers.push(user);
        }

        if (newUsers.length) savingPromises.push(commonApi.createUsers(newUsers));
        if (modifiedUsers.length) savingPromises.push(commonApi.updateUsers(modifiedUsers));

        await Promise.all(savingPromises);
        emitter.emit("users-updated", { modifiedUsers, newUsers });
      }
    } catch (error) {
      logger.error("Usermanager.saveUsers", "An error occurred while saving users", error).alsoToConsole();
      commit("set-section-error", { section: "users", error: error });
    }
  },

  saveRolesAndPermissions: async ({ state, commit, dispatch, rootState }) => {
    try {
      const savingPromises = [];

      if (state.deletedRoleIds.length) {
        savingPromises.push(rolesApi.delete(state.deletedRoleIds));
        await Promise.all(savingPromises);
      }

      if (state.roles.some((x) => x.isModified || x.isNew)) {
        const modifiedRoles = state.roles.filter((x) => x.isModified || x.isNew);

        if (modifiedRoles.length) {
          const promise = rolesApi.update(modifiedRoles);
          savingPromises.push(promise);

          promise.then(() => {
            const _permissions = modifiedRoles.filter((x) => !x.isLocked).map((x) => Object.assign({ roleId: x.roleId }, x.permissions));

            if (_permissions.length)
              permissionApi.update(_permissions).then(() => {
                if (
                  _.chain(modifiedRoles.map((x) => x.users))
                    .flatten()
                    .includes(rootState.user.userId)
                    .value()
                ) {
                  dispatch("loadUserProfile", true, { root: true });
                }
              });
          });
        }
      }
      await Promise.all(savingPromises);
    } catch (error) {
      logger.error("Usermanager.saveRolesAndPermissions", "An error occurred while saving roles and permissions", error).alsoToConsole();
      commit("set-section-error", { section: "rolesAndPermissions", error: error });
    }
  },

  saveLicensesAndAssignments: async ({ state, commit }) => {
    try {
      const savingPromises = [];

      if (state.licenses.some((x) => x.assignedUsers.some((u) => u.userLogonId == Guid.empty))) {
        const assignedUsers = state.licenses.filter((x) => x.assignedUsers.some((u) => u.userLogonId == Guid.empty));

        const promise = userLicenses.assign(assignedUsers);
        savingPromises.push(promise);

        promise.then((result) => {
          for (const licenseName in result) {
            for (const userIndex in result[licenseName]) {
              const license = state.licenses.find((x) => x.name == licenseName);
              if (license) {
                license.assignedUsers[userIndex].userLogonId = result[licenseName][userIndex] ?? null;
              }
            }
          }
        });
      }

      if (state.unassignedUsers.length) {
        savingPromises.push(userLicenses.unassign(state.unassignedUsers));
      }

      await Promise.all(savingPromises);
    } catch (error) {
      logger.error("Usermanager.saveLicensesAndAssignments", "An error occurred while saving licenses and assignments", error).alsoToConsole();
      commit("set-section-error", { section: "licenses", error: error });
    }
  },

  saveAll: async ({ dispatch, commit, getters }) => {
    try {
      commit("set-is-saving", true);
      commit("reset-errors");
      commit("notification-unseen");

      await dispatch("saveUsers");
      await dispatch("saveRolesAndPermissions");
      await dispatch("saveLicensesAndAssignments");
      if (getters.lastRunSuccess) {
        commit("clean-up-after-save");
        dispatch("loadUserProfile", true, { root: true });
      }
    } catch (error) {
      logger.error("Usermanager.saveAll", "An error occurred during the full save process", error).alsoToConsole();
      emitter.emit("global-error", error);
    } finally {
      commit("set-is-saving", false);
    }
  },
  setSectionError: function ({ commit }, args?: any) {
    commit("set-section-error", args);
  }
};

export default {
  namespaced: true,
  state: { ...defaultState() },
  mutations,
  getters,
  actions
};
